import { useMemo } from 'react'
import { View } from 'react-native'
import { useThemeColors } from '../lib/useThemeColors'
import { useAuth } from '../lib/AuthContext'
import PersonAvatar from './PersonAvatar'
import { PERSON_DEFAULT, lighten, personFromKit } from './personAvatarConfig'
import { useShowAvatars } from '../lib/avatarPref'

/**
 * Avatar dei corridori e maglie delle squadre (Pick, classifica). Tutto viene dai dati del DB e, dove mancano, da DEFAULT qui sotto:
 *  - RiderFace (default): volto tondo, per le liste con spazio (modale di scelta, 34-40 px);
 *  - RiderFace shape="bust": busto con maglia (quadrato dagli angoli tondi): sostituisce la vecchia icona maglia accanto al nome (slot, insight);
 *  - TeamJersey: icona della squadra = solo la maglia del kit su fondino chiaro: intestazioni squadra e righe piccole di classifica.
 * dim_rider.avatar = {gender?, style, hair, skin?, beard?, beard_a? (opacita' barba 0..1), glasses?, fringe?, face? (volto alternativo a..g, uomo e donna), brow? (1 sottili, 2 folte; solo con face), head? ({slug, v}: testa su misura scaricata da Supabase, vedi lib/avatarHeads.js)}; la maglia viene da dim_team.kit (vedi personFromKit).
 * Corridore senza avatar -> TEMPLATE (testa neutra, casco e occhiali: uomo calvo, donna con capelli corti); squadra senza kit -> DEFAULT_KIT
 * (nera con dettaglio rosa VC fisso, uguale in ogni tema). Niente di questo si scrive nel DB: quando arriva l'avatar/kit vero sostituisce il default.
 */
export const DEFAULT_KIT = { design: 'classic', colors: { main: '#111111', accent: '#E8397D', trim: '#E8397D' } }
const TEMPLATE = {
  man: { style: 'h4', headwear: 'helmet', glasses: true },
  woman: { style: 'h2', headwear: 'helmet', glasses: true },
}

const FACE = { crop: 0.66, x0: 0.17, y0: 0.0 }      // volto tondo: porzione dell'avatar intero
const BUST = { crop: 0.766, x0: 0.117, y0: 0.039 }   // busto: testa e spalle con la maglia
const BG_LIGHTEN = 0.8

// genere di ripiego (corridore senza avatar): quello della competizione attiva
function useFallbackGender() {
  const { competitionId } = useAuth()
  return competitionId === 2 ? 'woman' : 'man'
}

function usePerson(avatar, kit, gender) {
  const tc = useThemeColors()
  const fallback = useFallbackGender()
  return useMemo(() => {
    const k = kit ? personFromKit(kit) : personFromKit(DEFAULT_KIT)
    if (!avatar) {
      const g = gender ?? fallback
      return { ...PERSON_DEFAULT, gender: g, ...TEMPLATE[g], ...k }
    }
    const { style, hair, skin, beard, beard_a, glasses, gender: g, fringe, face, brow, flip, head } = avatar
    return {
      ...PERSON_DEFAULT, gender: g === 'woman' ? 'woman' : 'man', headwear: 'none', style: style ?? 'h1',
      ...(hair ? { hair } : null), ...(skin ? { skin } : null), ...(beard ? { beard } : null), ...(beard_a != null ? { beardA: Number(beard_a) } : null), glasses: !!glasses, fringe: !!fringe, ...(face ? { face } : null), ...(brow ? { brow } : null), ...(flip ? { flip: true } : null),
      ...(head?.slug ? { head } : null),   // testa su misura (lib/avatarHeads.js)
      ...k,
    }
  }, [avatar, kit, gender, fallback])
}

export default function RiderFace({ avatar, kit, size = 34, shape = 'face', gender }) {
  const tc = useThemeColors()
  const person = usePerson(avatar, kit, gender)
  const show = useShowAvatars()
  // interruttore del Profilo spento: al posto del corridore la maglia della squadra, nello stesso spazio
  if (!show) return <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}><TeamJersey kit={kit} size={size} gender={gender} /></View>
  // shape="full": avatar intero, senza ritaglio (oggi non usata nell'app: la galleria dei corridori e' nel pannello admin; il corridore alla massima dimensione)
  if (shape === 'full') {
    return (
      <View style={{ width: size, height: size, borderRadius: size / 14, overflow: 'hidden', backgroundColor: lighten(tc.rosa, BG_LIGHTEN) }}>
        <PersonAvatar person={person} size={size} />
      </View>
    )
  }
  const g = shape === 'bust' ? BUST : FACE
  const full = size / g.crop
  return (
    <View style={{ width: size, height: size, borderRadius: shape === 'bust' ? size / 5 : size / 2, overflow: 'hidden', backgroundColor: lighten(tc.rosa, BG_LIGHTEN) }}>
      <View style={{ marginLeft: -full * g.x0, marginTop: -full * g.y0 }}>
        <PersonAvatar person={person} size={full} />
      </View>
    </View>
  )
}

// Icona della squadra: solo la maglia del kit (niente testa, collo o braccia) su un fondino chiaro, al posto di dim_team.jersey_url.
// `size` = larghezza del riquadro (altezza = 0.72 x larghezza). Si distingue dai volti dei corridori, che stanno nelle righe sotto.
// Ritaglio della maglia nell'avatar intero (512 px): x 68..442, y 254..512 = dal bordo alto del colletto (sopra c'e' il contorno del collo; le righe 250-253 del livello trim sono il retro del colletto, coperto dal collo nell'avatar intero, e fuori dal ritaglio non si vede il "trattino") fino in fondo
// al tronco (fine del canvas). Le linee delle braccia sotto i polsini sono tolte da jersey_line_tile (ritoccato a mano), quindi il tronco si vede per intero.
const TILE = { x0: 68 / 512, y0: 254 / 512, w: 374 / 512, h: 258 / 512, ratio: 0.78, pad: 4 }
export function TeamJersey({ kit, size = 56, gender }) {
  const tc = useThemeColors()
  const person = usePerson(null, kit, gender)
  const w = size, h = Math.round(size * TILE.ratio)
  const full = (w - TILE.pad * 2) / TILE.w
  const cw = full * TILE.w, ch = full * TILE.h
  // finestra che taglia davvero la maglia ai bordi misurati (altrimenti il riquadro, piu' alto del ritaglio, mostrerebbe arco del collo e braccia)
  return (
    <View style={{ width: w, height: h, borderRadius: h / 3, overflow: 'hidden', backgroundColor: lighten(tc.rosa, BG_LIGHTEN), alignItems: 'center', justifyContent: 'center' }}>
      <View style={{ width: cw, height: ch, overflow: 'hidden' }}>
        <View style={{ marginLeft: -full * TILE.x0, marginTop: -full * TILE.y0 }}>
          <PersonAvatar person={person} size={full} jerseyOnly />
        </View>
      </View>
    </View>
  )
}
